import {
  ArrowLeft,
  Target,
  Dumbbell,
  Timer,
  TrendingUp,
  CheckCircle,
  Flame,
  Activity,
  Bike,
  Footprints,
  Waves,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import welcomeBg from "../../assets/homebg.png";

// ─── Exercise Types ────────────────────────────────────────────────────────────
const EXERCISE_TYPES = [
  { id: "running",  label: "Running",  icon: Footprints, color: "bg-orange-500",  light: "bg-orange-50 dark:bg-orange-900/20",  border: "border-orange-300 dark:border-orange-700", text: "text-orange-600 dark:text-orange-400", metPerMin: 9 },
  { id: "walking",  label: "Walking",  icon: Activity,   color: "bg-green-500",   light: "bg-green-50 dark:bg-green-900/20",    border: "border-green-300 dark:border-green-700",   text: "text-green-600 dark:text-green-400",  metPerMin: 4 },
  { id: "cycling",  label: "Cycling",  icon: Bike,       color: "bg-blue-500",    light: "bg-blue-50 dark:bg-blue-900/20",      border: "border-blue-300 dark:border-blue-700",     text: "text-blue-600 dark:text-blue-400",    metPerMin: 7 },
  { id: "swimming", label: "Swimming", icon: Waves,      color: "bg-cyan-500",    light: "bg-cyan-50 dark:bg-cyan-900/20",      border: "border-cyan-300 dark:border-cyan-700",     text: "text-cyan-600 dark:text-cyan-400",    metPerMin: 8 },
  { id: "gym",      label: "Gym",      icon: Dumbbell,   color: "bg-indigo-500",  light: "bg-indigo-50 dark:bg-indigo-900/20",  border: "border-indigo-300 dark:border-indigo-700", text: "text-indigo-600 dark:text-indigo-400", metPerMin: 5 },
  { id: "other",    label: "Other",    icon: Plus,       color: "bg-purple-500",  light: "bg-purple-50 dark:bg-purple-900/20",  border: "border-purple-300 dark:border-purple-700", text: "text-purple-600 dark:text-purple-400", metPerMin: 5 },
];

const INTENSITY_LEVELS = [
  { id: "low",    label: "Low",    multiplier: 0.7, color: "bg-green-500",  textActive: "text-white", bg: "bg-green-500",  lightBg: "bg-green-50 dark:bg-green-900/20",  border: "border-green-400" },
  { id: "medium", label: "Medium", multiplier: 1.0, color: "bg-orange-500", textActive: "text-white", bg: "bg-orange-500", lightBg: "bg-orange-50 dark:bg-orange-900/20", border: "border-orange-400" },
  { id: "high",   label: "High",   multiplier: 1.4, color: "bg-red-500",    textActive: "text-white", bg: "bg-red-500",    lightBg: "bg-red-50 dark:bg-red-900/20",      border: "border-red-400" },
];

// ─── Suggested Workouts ────────────────────────────────────────────────────────
const WORKOUTS = [
  { id: 1, title: "Morning Energy Boost",    description: "Start your day with this energizing routine to kickstart your metabolism", duration: "15 min", exercises: ["Jumping Jacks", "Push-ups", "Squats", "Plank"],               calories: "120 cal", difficulty: "Beginner",     icon: "☀️", color: "from-orange-100 to-orange-50 dark:from-orange-900/20 dark:to-orange-900/10" },
  { id: 2, title: "Core Crusher",            description: "Targeted ab workout to strengthen your core and improve posture",         duration: "20 min", exercises: ["Crunches", "Russian Twists", "Leg Raises", "Mountain Climbers"], calories: "180 cal", difficulty: "Intermediate", icon: "💪", color: "from-blue-100 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/10" },
  { id: 3, title: "Cardio Blast",            description: "High-intensity cardio to burn calories and improve cardiovascular health", duration: "25 min", exercises: ["Burpees", "High Knees", "Jump Rope", "Sprint Intervals"],         calories: "300 cal", difficulty: "Advanced",     icon: "🔥", color: "from-red-100 to-red-50 dark:from-red-900/20 dark:to-red-900/10" },
  { id: 4, title: "Flexibility & Stretch",   description: "Gentle stretching routine to improve flexibility and reduce muscle tension", duration: "12 min", exercises: ["Forward Bend", "Cat-Cow", "Hip Flexor Stretch", "Shoulder Stretch"], calories: "60 cal",  difficulty: "Beginner",     icon: "🧘", color: "from-green-100 to-green-50 dark:from-green-900/20 dark:to-green-900/10" },
  { id: 5, title: "Upper Body Strength",     description: "Build strength in your arms, shoulders, and chest",                     duration: "30 min", exercises: ["Dumbbell Press", "Rows", "Bicep Curls", "Tricep Dips"],            calories: "220 cal", difficulty: "Intermediate", icon: "💪", color: "from-indigo-100 to-indigo-50 dark:from-indigo-900/20 dark:to-indigo-900/10" },
  { id: 6, title: "Lower Body Power",        description: "Strengthen your legs and glutes with this effective lower body workout",  duration: "28 min", exercises: ["Lunges", "Deadlifts", "Calf Raises", "Leg Press"],               calories: "250 cal", difficulty: "Advanced",     icon: "🦵", color: "from-purple-100 to-purple-50 dark:from-purple-900/20 dark:to-purple-900/10" },
];

// ─── Component ─────────────────────────────────────────────────────────────────
const SuggestedWorkouts = () => {
  const navigate = useNavigate();
  const [completedWorkouts, setCompletedWorkouts] = useState<number[]>([]);
  const [showManualForm, setShowManualForm] = useState(true);

  // Manual exercise form state
  const [selectedType, setSelectedType]       = useState("running");
  const [exerciseName, setExerciseName]       = useState("");
  const [duration, setDuration]               = useState("");
  const [intensity, setIntensity]             = useState("medium");
  const [manualCalories, setManualCalories]   = useState("");
  const [loggedExercises, setLoggedExercises] = useState<{ name: string; duration: string; calories: string; type: string }[]>([]);

  // Auto-calculate estimated calories
  const typeObj      = EXERCISE_TYPES.find(t => t.id === selectedType)!;
  const intensityObj = INTENSITY_LEVELS.find(i => i.id === intensity)!;
  const estimatedCal = duration
    ? Math.round(Number(duration) * typeObj.metPerMin * intensityObj.multiplier)
    : 0;

  const handleLogExercise = () => {
    if (!duration || Number(duration) <= 0) {
      toast.error("Please enter a valid duration.");
      return;
    }
    const name = exerciseName.trim() || typeObj.label;
    const cal  = manualCalories || String(estimatedCal);
    setLoggedExercises(prev => [{ name, duration, calories: cal, type: selectedType }, ...prev]);
    toast.success(`✅ ${name} logged — ${cal} kcal burned!`);
    setExerciseName("");
    setDuration("");
    setManualCalories("");
  };

  const handleComplete = (id: number, title: string) => {
    setCompletedWorkouts(prev => [...prev, id]);
    toast.success(`Completed: ${title}!`);
  };

  const getDifficultyColor = (d: string) =>
    d === "Beginner"     ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400" :
    d === "Intermediate" ? "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400" :
                           "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400";

  return (
    <div className="scrollbar-hide relative mx-auto flex h-dvh max-h-dvh w-full min-w-0 max-w-[400px] flex-col overflow-hidden bg-background">
      {/* Blurred background */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      <div className="absolute inset-0 bg-background/85" />

      <div className="scrollbar-hide relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto w-full max-w-[400px] space-y-3 p-4 pb-24">

        {/* ── Header ── */}
        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-10 w-10 rounded-xl border border-border bg-card">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title3 font-bold text-foreground">Exercise</h1>
          <div className="w-10" />
        </div>

        {/* ══════════════════════════════════════════════════
            MANUAL EXERCISE INPUT CARD
        ══════════════════════════════════════════════════ */}
        <div className="rounded-2xl border border-indigo-200 dark:border-indigo-800/40 bg-card shadow-sm overflow-hidden">
          {/* Card Header — toggle */}
          <button
            onClick={() => setShowManualForm(v => !v)}
            className="flex w-full items-center justify-between px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20"
          >
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-500 shadow-sm shadow-indigo-200 dark:shadow-indigo-900/40">
                <Dumbbell className="h-4 w-4 text-white" />
              </div>
              <div className="text-left">
                <p className="text-subhead font-bold text-foreground">Log Manual Exercise</p>
                <p className="text-compact-sm text-muted-foreground">Track any workout you did</p>
              </div>
            </div>
            {showManualForm
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>

          {showManualForm && (
            <div className="px-4 pb-4 pt-3 space-y-4">

              {/* Exercise Type Selector */}
              <div>
                <p className="mb-2 text-caption1 font-semibold uppercase tracking-wide text-muted-foreground">Exercise Type</p>
                <div className="grid grid-cols-3 gap-2">
                  {EXERCISE_TYPES.map(t => {
                    const Icon = t.icon;
                    const active = selectedType === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setSelectedType(t.id)}
                        className={`flex flex-col items-center gap-1.5 rounded-xl border py-2.5 transition-all active:scale-95 ${
                          active
                            ? `${t.light} ${t.border} shadow-sm`
                            : "border-border bg-background hover:bg-muted/40"
                        }`}
                      >
                        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${active ? t.color : "bg-muted"}`}>
                          <Icon className={`h-4 w-4 ${active ? "text-white" : "text-muted-foreground"}`} />
                        </div>
                        <span className={`text-compact-sm font-semibold ${active ? t.text : "text-muted-foreground"}`}>
                          {t.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Exercise Name + Duration Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-caption1 font-semibold text-muted-foreground">Exercise Name</label>
                  <input
                    type="text"
                    value={exerciseName}
                    onChange={e => setExerciseName(e.target.value)}
                    placeholder={typeObj.label}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-subhead text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-caption1 font-semibold text-muted-foreground">Duration (min)</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    value={duration}
                    onChange={e => setDuration(e.target.value)}
                    placeholder="30"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-subhead text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              </div>

              {/* Intensity Selector */}
              <div>
                <p className="mb-2 text-caption1 font-semibold uppercase tracking-wide text-muted-foreground">Intensity</p>
                <div className="grid grid-cols-3 gap-2">
                  {INTENSITY_LEVELS.map(lvl => {
                    const active = intensity === lvl.id;
                    return (
                      <button
                        key={lvl.id}
                        onClick={() => setIntensity(lvl.id)}
                        className={`rounded-xl border py-2 text-subhead font-semibold transition-all active:scale-95 ${
                          active
                            ? `${lvl.bg} border-transparent text-white shadow-sm`
                            : "border-border bg-background text-muted-foreground hover:bg-muted/40"
                        }`}
                      >
                        {lvl.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Calories Row */}
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="mb-1.5 block text-caption1 font-semibold text-muted-foreground">
                    Calories (kcal)
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={manualCalories}
                    onChange={e => setManualCalories(e.target.value)}
                    placeholder={estimatedCal > 0 ? String(estimatedCal) : "Auto"}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-subhead text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
                {/* Estimated display */}
                {estimatedCal > 0 && (
                  <div className="rounded-xl border border-orange-200 dark:border-orange-800/40 bg-orange-50 dark:bg-orange-900/20 px-3 py-2.5 flex items-center gap-2">
                    <Flame className="h-4 w-4 text-orange-500 shrink-0" />
                    <div>
                      <p className="text-compact-xs text-muted-foreground leading-none mb-0.5">Estimated</p>
                      <p className="text-subhead font-bold text-orange-600 dark:text-orange-400">{estimatedCal} kcal</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Log Button */}
              <Button
                onClick={handleLogExercise}
                className="h-11 w-full rounded-xl bg-indigo-500 text-subhead font-semibold text-white hover:bg-indigo-600 active:scale-[0.98] transition-all"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Log Exercise
              </Button>
            </div>
          )}
        </div>

        {/* ── Logged Exercises ── */}
        {loggedExercises.length > 0 && (
          <div className="space-y-2">
            <p className="text-caption1 font-semibold uppercase tracking-wide text-muted-foreground px-1">Today's Logged</p>
            {loggedExercises.map((ex, i) => {
              const t = EXERCISE_TYPES.find(t => t.id === ex.type)!;
              const Icon = t.icon;
              return (
                <div key={i} className={`flex items-center gap-3 rounded-xl border ${t.border} ${t.light} px-3 py-2.5`}>
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.color}`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-subhead font-semibold text-foreground truncate">{ex.name}</p>
                    <p className="text-caption1 text-muted-foreground">{ex.duration} min</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Flame className="h-3.5 w-3.5 text-orange-500" />
                    <span className="text-subhead font-bold text-orange-600 dark:text-orange-400">{ex.calories}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Suggested Workouts ── */}
        <div className="flex items-center gap-2 pt-1">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <Target className="h-4 w-4 text-blue-500" />
          </div>
          <p className="text-subhead font-bold text-foreground">Suggested Workouts</p>
        </div>

        <div className="space-y-3">
          {WORKOUTS.map(workout => {
            const isCompleted = completedWorkouts.includes(workout.id);
            return (
              <Card key={workout.id} className={`rounded-2xl border-border p-4 shadow-sm transition-all ${isCompleted ? "opacity-60" : "hover:shadow-md"}`}>
                <div className={`bg-gradient-to-br ${workout.color} relative mb-3 overflow-hidden rounded-xl p-3`}>
                  <div className="mb-2 flex items-start justify-between">
                    <div className="text-title1">{workout.icon}</div>
                    <Badge variant="outline" className={`text-caption1 ${getDifficultyColor(workout.difficulty)}`}>
                      {workout.difficulty}
                    </Badge>
                  </div>
                  <h3 className="text-callout font-bold text-foreground mb-0.5">{workout.title}</h3>
                  <p className="text-caption1 text-muted-foreground">{workout.description}</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-caption1 text-muted-foreground">
                    <div className="flex items-center gap-1"><Timer className="h-3.5 w-3.5" />{workout.duration}</div>
                    <div className="flex items-center gap-1"><Flame className="h-3.5 w-3.5 text-orange-500" />{workout.calories}</div>
                    <div className="flex items-center gap-1"><Dumbbell className="h-3.5 w-3.5" />{workout.exercises.length} exercises</div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {workout.exercises.map((ex, i) => (
                      <Badge key={i} variant="secondary" className="text-compact-sm">{ex}</Badge>
                    ))}
                  </div>

                  <Button
                    className={`h-10 w-full rounded-xl text-subhead font-semibold ${isCompleted ? "" : "bg-indigo-500 hover:bg-indigo-600 text-white"}`}
                    onClick={() => handleComplete(workout.id, workout.title)}
                    disabled={isCompleted}
                  >
                    {isCompleted ? (<><CheckCircle className="mr-2 h-4 w-4" />Completed</>) : "Start Workout"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* ── Today's Progress ── */}
        <Card className="rounded-2xl border-border p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-subhead font-bold text-foreground">Today's Progress</p>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-subhead">
              <span className="text-muted-foreground">Workouts Completed</span>
              <span className="font-semibold text-foreground">{completedWorkouts.length} / 3</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted/30">
              <div className="h-2 rounded-full bg-indigo-500 transition-all" style={{ width: `${Math.min((completedWorkouts.length / 3) * 100, 100)}%` }} />
            </div>
            <p className="text-caption1 text-muted-foreground">
              {completedWorkouts.length === 0 && "Start your first workout to begin!"}
              {completedWorkouts.length > 0 && completedWorkouts.length < 3 && "Keep going! You're making great progress."}
              {completedWorkouts.length >= 3 && "Amazing! You've reached your daily goal! 🎉"}
            </p>
          </div>
        </Card>

      </div>
      </div>
    </div>
  );
};

export default SuggestedWorkouts;
